<script setup>
import { ref, computed, reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import PasswordRequirements from './PasswordRequirements.vue';
import GoogleOAuthButton from '../../../../../components/GoogleOauth/Button.vue';
import { register } from '../../../../../api/auth';
import { getLoginRedirectURL } from '../../../../../helpers/AuthHelper';

const MIN_PASSWORD_LENGTH = 6;
const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{}|'"/\\.,`<>:;?~]/;
const noSpecialChars = value => !SPECIAL_CHAR_REGEX.test(value || '');

const store = useStore();
const { t } = useI18n();
const router = useRouter();

const hCaptcha = ref(null);
const isPasswordFocused = ref(false);
const isSignupInProgress = ref(false);

const credentials = reactive({
  email: '',
  password: '',
  hCaptchaClientResponse: '',
});

const rules = {
  credentials: {
    email: {
      required,
      email,
    },
    password: {
      required,
      minLength: minLength(MIN_PASSWORD_LENGTH),
      noSpecialChars,
    },
  },
};

const v$ = useVuelidate(rules, { credentials });

const globalConfig = computed(() => store.getters['globalConfig/get']);

const termsLink = computed(() =>
  t('REGISTER.TERMS_ACCEPT')
    .replace('https://www.chatwoot.com/terms', globalConfig.value.termsURL)
    .replace(
      'https://www.chatwoot.com/privacy-policy',
      globalConfig.value.privacyURL
    )
);

const allowedLoginMethods = computed(
  () => window.chatwootConfig.allowedLoginMethods || ['email']
);

const showGoogleOAuth = computed(
  () =>
    allowedLoginMethods.value.includes('google_oauth') &&
    Boolean(window.chatwootConfig.googleOAuthClientId)
);

const isFormValid = computed(() => !v$.value.$invalid);

const performRegistration = async () => {
  isSignupInProgress.value = true;
  try {
    const response = await register(credentials);
    // 自研: 注册响应若已含已认证用户的 accounts (例如直接激活流程),
    // 按 activation_status 决定跳转 (待支付/支付完成/dashboard)。
    // 默认 anonymous 注册只返回 { email }, 走 verify_email 流程。
    const userData = response?.data;
    if (userData?.accounts?.length) {
      window.location = getLoginRedirectURL({ user: userData });
    } else {
      router.push({
        name: 'auth_verify_email',
        state: { email: credentials.email },
      });
    }
  } catch (error) {
    const errorMessage = error?.message || t('REGISTER.API.ERROR_MESSAGE');
    if (globalConfig.value.hCaptchaSiteKey) {
      hCaptcha.value.reset();
      credentials.hCaptchaClientResponse = '';
    }
    useAlert(errorMessage);
  } finally {
    isSignupInProgress.value = false;
  }
};

const submit = () => {
  if (isSignupInProgress.value) return;
  v$.value.$touch();
  if (v$.value.$invalid) return;
  isSignupInProgress.value = true;
  if (globalConfig.value.hCaptchaSiteKey) {
    hCaptcha.value.execute();
  } else {
    performRegistration();
  }
};

const onRecaptchaVerified = token => {
  credentials.hCaptchaClientResponse = token;
  performRegistration();
};

const onCaptchaError = () => {
  isSignupInProgress.value = false;
  credentials.hCaptchaClientResponse = '';
  hCaptcha.value.reset();
};
</script>

<!-- 与登录页一致的 native input 风格, inline-style 待 Tailwind 化 -->
<!-- eslint-disable vue/no-static-inline-styles -->
<template>
  <div class="flex-1">
    <form class="space-y-5" @submit.prevent="submit">
      <!-- Email Field -->
      <div>
        <label
          for="email_address"
          class="block mb-2 text-sm font-medium"
          style="color: #1f2937"
        >
          {{ $t('REGISTER.EMAIL.LABEL') }}
        </label>
        <input
          id="email_address"
          v-model="credentials.email"
          name="email_address"
          type="email"
          :placeholder="$t('REGISTER.EMAIL.PLACEHOLDER')"
          class="w-full px-4 py-3 text-base rounded-lg outline-none"
          style="
            background-color: #ffffff;
            border: 1px solid #e5e7eb;
            color: #1f2937;
          "
          :style="v$.credentials.email.$error ? 'border-color: #ef4444;' : ''"
          @input="v$.credentials.email.$touch"
          @focus="$event.target.style.borderColor = '#1f93ff'"
          @blur="
            $event.target.style.borderColor = v$.credentials.email.$error
              ? '#ef4444'
              : '#e5e7eb'
          "
        />
        <p
          v-if="v$.credentials.email.$error"
          class="mt-1 text-xs"
          style="color: #ef4444"
        >
          {{ $t('REGISTER.EMAIL.ERROR') }}
        </p>
      </div>

      <!-- Password Field -->
      <div class="relative">
        <label
          for="password"
          class="block mb-2 text-sm font-medium"
          style="color: #1f2937"
        >
          {{ $t('LOGIN.PASSWORD.LABEL') }}
        </label>
        <input
          id="password"
          v-model="credentials.password"
          name="password"
          type="password"
          :placeholder="$t('SET_NEW_PASSWORD.PASSWORD.PLACEHOLDER')"
          class="w-full px-4 py-3 text-base rounded-lg outline-none"
          style="
            background-color: #ffffff;
            border: 1px solid #e5e7eb;
            color: #1f2937;
          "
          :style="
            v$.credentials.password.$error ? 'border-color: #ef4444;' : ''
          "
          @focus="
            isPasswordFocused = true;
            $event.target.style.borderColor = '#1f93ff';
          "
          @blur="
            isPasswordFocused = false;
            v$.credentials.password.$touch();
            $event.target.style.borderColor = v$.credentials.password.$error
              ? '#ef4444'
              : '#e5e7eb';
          "
        />
        <Transition
          enter-active-class="transition duration-200 ease-out origin-left"
          enter-from-class="opacity-0 scale-90 translate-x-1"
          leave-active-class="transition duration-150 ease-in origin-left"
          leave-to-class="opacity-0 scale-90 translate-x-1"
        >
          <PasswordRequirements
            v-if="isPasswordFocused"
            :password="credentials.password"
          />
        </Transition>
      </div>

      <VueHcaptcha
        v-if="globalConfig.hCaptchaSiteKey"
        ref="hCaptcha"
        size="invisible"
        :sitekey="globalConfig.hCaptchaSiteKey"
        @verify="onRecaptchaVerified"
        @error="onCaptchaError"
        @expired="onCaptchaError"
        @challenge-expired="onCaptchaError"
        @closed="onCaptchaError"
      />

      <button
        type="submit"
        data-testid="submit_button"
        :disabled="isSignupInProgress || !isFormValid"
        class="w-full py-3 text-base font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        style="background-color: #1f93ff"
        @mouseenter="
          if (!isSignupInProgress && isFormValid)
            $event.target.style.backgroundColor = '#1a7fd9';
        "
        @mouseleave="$event.target.style.backgroundColor = '#1f93ff'"
      >
        <span v-if="!isSignupInProgress">{{ $t('REGISTER.SUBMIT') }}</span>
        <span v-else>...</span>
      </button>
    </form>
    <GoogleOAuthButton v-if="showGoogleOAuth" class="mt-4">
      {{ $t('REGISTER.OAUTH.GOOGLE_SIGNUP') }}
    </GoogleOAuthButton>
    <p
      class="text-sm mt-5 mb-0 [&>a]:font-medium"
      style="color: #6b7280"
      v-html="termsLink"
    />
  </div>
</template>
